<template>
  <aside class="sidebar">
    <div class="sidebar-top">
      <router-link to="/" class="sidebar-logo">
        <img src="@/assets/svg/kodland-logo.svg" alt="kodland" />
      </router-link>
    </div>
    <div class="sidebar-content">
      <nav class="sidebar-menu">
        <div class="sidebar-time" v-if="currentSegment">
          <span class="sidebar-menu__time">
            {{ $t("sidebar.time.title") }}: {{ localTime }}
          </span>
          <span class="sidebar-time__title">{{ timezoneName }}</span>
        </div>
        <router-link to="/master-classes/" class="sidebar-menu-link">
          <inline-svg
            :src="require('@/assets/svg/menu-mk.svg')"
            class="sidebar-menu-link__icon" />
          <span class="sidebar-menu-link__text">
            {{ $t("sidebar.menu.mc") }}
          </span>
        </router-link>
        <div
          @click="openSalaryModal"
          v-if="isMimk"
          class="mimk-wallet flex-column">
          <div class="mimk-wallet__block">
            <div class="mimk-wallet__text">
              <div class="mimk-wallet__svg">
                <img svg-inline src="@/assets/svg/mimk-wallet.svg" />
              </div>
              <span class="mimk-wallet__title">
                {{ $t("header.payment.title") }}
              </span>
            </div>
          </div>
          <div class="mimk-wallet__block mimk-wallet__amount">
            <span v-if="currentAward" class="mimk-wallet__salary">
              {{ currentAward.award }} {{ currentAward.currency }}
            </span>
            <span v-if="!currentAward" class="mimk-wallet__salary">
              {{ $t("header.payment.unknown") }}
            </span>
          </div>
        </div>
        <span class="sidebar-menu-link" v-if="isAdmin">
          <inline-svg
            :src="require('@/assets/svg/admin.svg')"
            class="sidebar-menu-link__icon" />
          <span
            class="sidebar-menu-link__text"
            @click="openSidebarLinks = !openSidebarLinks">
            {{ $t("sidebar.menu.settingsAdmin") }}
          </span>
        </span>
        <div v-if="openSidebarLinks">
          <router-link to="/overbooking/" class="sidebar-menu-link">
            <span
              class="sidebar-menu-link__text"
              v-html="$t('overbooking.sidebarTitle')" />
          </router-link>
          <router-link to="/time-setting/" class="sidebar-menu-link">
            <span
              class="sidebar-menu-link__text"
              v-html="$t('sidebar.menu.settingTime')" />
          </router-link>
          <router-link to="/admin-redis/" class="sidebar-menu-link">
            <span class="sidebar-menu-link__text">Redis</span>
          </router-link>
          <router-link to="/rating-setting/" class="sidebar-menu-link">
            <span
              class="sidebar-menu-link__text"
              v-html="$t('sidebar.menu.settingsRating')" />
          </router-link>
        </div>
      </nav>
      <div class="sidebar-selects">
        <div class="sidebar-selects__block" v-if="isAdmin && !isOverbooking">
          <span class="sidebar-selects__title">
            {{ $t("sidebar.select.role") }}:
          </span>
          <v-select
            :options="roles"
            :clearable="false"
            :searchable="false"
            label="title"
            class="sidebar-select"
            @input="changeRole"
            v-model="selectedRole">
            <template #selected-option="{ id }">
              <div class="sidebar-select-container">
                <div class="sidebar-select-content">
                  <span class="sidebar-select__selected">
                    {{ $t("role." + id) }}
                  </span>
                </div>
                <inline-svg
                  class="sidebar-select__arrow"
                  :src="require(`@/assets/svg/langs/arrow.svg`)" />
              </div>
            </template>
            <template #option="{ title }">
              <div class="sidebar-select-option">
                <span class="sidebar-select-option__name">{{ title }}</span>
              </div>
            </template>
          </v-select>
        </div>
        <div class="sidebar-selects__block" v-if="currentSegment && !isMimk">
          <span class="sidebar-selects__title">
            {{ $t("sidebar.select.segment") }}:
          </span>
          <v-select
            :options="$store.state.user.segmentsList"
            :clearable="false"
            :searchable="false"
            label="title"
            class="sidebar-select"
            @input="changeSegment"
            v-model="currentSegment">
            <template #selected-option="{ title }">
              <div class="sidebar-select-container">
                <div class="sidebar-select-content">
                  <span class="sidebar-select__selected">{{ title }}</span>
                </div>
                <inline-svg
                  class="sidebar-select__arrow"
                  :src="require(`@/assets/svg/langs/arrow.svg`)" />
              </div>
            </template>
            <template #option="{ title }">
              <div class="sidebar-select-option">
                <span class="sidebar-select-option__name">{{ title }}</span>
              </div>
            </template>
          </v-select>
        </div>
        <div class="sidebar-selects__block">
          <span class="sidebar-selects__title">
            {{ $t("sidebar.select.language") }}:
          </span>
          <v-select
            :options="$store.state.langs.langs"
            :clearable="false"
            :searchable="false"
            label="name"
            class="sidebar-select"
            v-model="currentLang"
            @input="changeLang">
            <template #selected-option="{ icon, name }">
              <div class="sidebar-select-container">
                <div class="sidebar-select-content">
                  <inline-svg class="sidebar-select__icon" :src="icon" />
                  <span class="sidebar-select__selected">{{ name }}</span>
                </div>
                <inline-svg
                  class="sidebar-select__arrow"
                  :src="require(`@/assets/svg/langs/arrow.svg`)" />
              </div>
            </template>
            <template #option="{ icon, name }">
              <div class="sidebar-select-option">
                <img class="sidebar-select-option__icon" :src="icon" />
                <span class="sidebar-select-option__name">{{ name }}</span>
              </div>
            </template>
          </v-select>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import militaryAndLanguage from "../helpers/militaryAndLanguage";
import DatePicker from "vue2-datepicker";
import i18n from "@/helpers/i18n";
import choiseSelectTitleOnLoad from "../helpers/selects";
import cookie from "js-cookie";

function getCurrentMonth() {
  const today = new Date();
  const month = today.getMonth() + 1;
  if (month < 10) {
    return `${today.getFullYear()}-0${month}-01`;
  }
  return `${today.getFullYear()}-${month}-01`;
}

export default {
  name: "Sidebar",
  data() {
    return {
      choiseSelectTitleOnLoad,
      militaryAndLanguage,
      answer: null,
      typeOfChangedSelect: "",
      localTime: "",
      segments: [],
      selectedRole: "",
      formatedTime: null,
      formatLocale: null,
      currentSegment: {},
      currentLang: {},
      openSidebarLinks: false,
    };
  },

  created() {
    const userState = this.$store.state.user;

    this.currentSegment = userState.segment;
    this.currentLang = userState.lang;

    //Взаимодействие с селектом выбора ролей
    if (this.isAdmin) this.adminRoleManipulation();
    this.formatedTime = this.receiveTimeFromat(
      this.isMilitaryTime,
      userState.segment.segment_local_time
    ).formatedTime;
    this.formatLocale = this.receiveTimeFromat(
      this.isMilitaryTime,
      userState.segment.segment_local_time
    ).formatDependForLocale;
    this.localTime = this.formatedTime;
    setInterval(this.timeRun, 1000);
  },
  computed: {
    currentAward() {
      if (!this.isMimk) {
        return null;
      }
      const mimkAwards = this.$store.state.user.user.mimkAward;
      if (!mimkAwards) {
        return null;
      }
      if (!mimkAwards.data) {
        return null;
      }

      const currentDateMonth = getCurrentMonth();
      const awards = mimkAwards.data.filter(
        (x) => x.date_month === currentDateMonth
      );
      return awards ? awards[0] : null;
    },
    isTestMimk() {
      return cookie.get("testMimkInfo") === "test_mimk_84069";
    },
    userRole() {
      return this.$store.state.user.user.role;
    },
    isOverbooking() {
      return this.$route.name === "overbooking" ? true : false;
    },
    isMilitaryTime() {
      let military = null;
      military = this.militaryAndLanguage();
      return military;
    },
    timezoneName() {
      return this.$store.state.user.segment.timezone_name;
    },
    isAdmin() {
      return this.$store.state.user.user.role === "admin";
    },
    isMimk() {
      return this.$store.state.user.user.role === "mimk";
    },
    roles() {
      return [
        {
          id: "supervisor",
          title: this.$t("role.supervisor"),
        },
        {
          id: "manager",
          title: this.$t("role.manager"),
        },
        {
          id: "mimk",
          title: this.$t("role.mimk"),
        },
      ];
    },
  },
  methods: {
    openSalaryModal() {
      try {
        this.$metrika.reachGoal("TMC_click_salary_statistics");
      } catch (err) {
        console.log(err);
      }
      this.$store.commit("globalModals/setSalaryModal", true);
    },
    receiveTimeFromat(isMilitaryTime, hour, acceptedParam) {
      let formatHour = "";
      isMilitaryTime ? (formatHour = "HH:mm:ss") : (formatHour = "hh:mm:ss a");
      return {
        formatedTime: this.$dayjs(hour, acceptedParam).format(formatHour),
        formatDependForLocale: formatHour,
      };
    },
    timeRun() {
      this.localTime = this.$dayjs(this.localTime, this.formatLocale).add(
        1,
        "second"
      );
      this.localTime = this.$dayjs(this.localTime).format(this.formatLocale);
    },
    // logout() {
    //   cookie.remove("access");
    //   cookie.remove("refresh");
    //   cookie.remove("role");
    //   cookie.remove("my_cookie");
    //   if (cookie.get("authMimk")) {
    //     cookie.remove("authMimk");
    //   }
    //   window.location.href = "https://backoffice.kodland.org/ru/login/";
    // },
    adminRoleManipulation() {
      if (this.$route.meta.role?.id) {
        this.selectedRole = this.roles.find(
          (el) => el.id === this.$route.meta.role.id
        );

        localStorage.setItem("bookingRole", this.selectedRole);
      }
      if (!this.$route.meta.role?.id)
        this.selectedRole = this.roles.find(
          (el) => el.id === localStorage.getItem("bookingRole")
        );
    },
    changeRole(item) {
      localStorage.setItem("bookingRole", item.id);
      if (item.id === "admin") return;
      if (item.id === "manager")
        this.$router.push("/master-classes/manager/master-classes");
      else this.$router.push(`/master-classes/${item.id}/current-and-features`);
    },
    functionalAfterChangeLang(data) {
      localStorage.setItem("bookingLang", data.id);
      i18n.locale = data.id;
      DatePicker.locale(data.id);
      this.$router.go();
    },
    functionalAfterChangeSegment(data) {
      localStorage.setItem("bookingSegment", data.id);
      this.$router.go();
    },
    changeLang(changedSelectItem) {
      if (this.$store.state.booking.isChangedDataOnMatrixTable) {
        this.answer = window.confirm(this.$t("overbooking.confirm"));
        this.answer
          ? this.functionalAfterChangeLang(changedSelectItem)
          : (this.currentLang = this.choiseSelectTitleOnLoad(
              "bookingLang",
              this.$store.state.langs.langs
            ));
      } else this.functionalAfterChangeLang(changedSelectItem);
    },
    changeSegment(changedSelectItem) {
      if (this.$store.state.booking.isChangedDataOnMatrixTable) {
        this.answer = window.confirm(this.$t("overbooking.confirm"));
        if (this.answer) this.functionalAfterChangeSegment(changedSelectItem);
        else this.currentSegment = this.$store.state.user.segment;
      } else this.functionalAfterChangeSegment(changedSelectItem);
    },
  },
};
</script>

<style lang="scss" scoped>
.mimk-wallet {
  overflow: hidden;
  border-radius: 10px;
  margin-top: 30px;
  cursor: pointer;
  &__block {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 50px;
    text-align: center;
    &:first-child {
      background-color: #fac710;
    }
  }
  &__text {
    display: flex;
  }
  &__amount {
    background-color: #fff;
  }
  &__payment-block {
    width: 100%;
    text-align: center;
  }
  &__payment-info {
    height: 100%;
    justify-content: center;
  }
  &__title {
    font-weight: 600;
    margin-bottom: 5px;
  }
  &__salary {
    font-weight: 700;
  }
  &__svg {
    // height: 100%;
    // display: flex;
    // width: 30px;
    & img {
      width: 40px;
    }
  }
}
.log-out {
  cursor: pointer;
}
.sidebar {
  background: var(--sidebar);
  min-height: 100vh;
  min-width: 235px;
  max-width: 235px;
  padding: 24px;
  transition: 0.5s;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  &-time {
    text-align: center;
    margin-bottom: 10px;
    &__title {
      align-items: end;
      font-size: 12px;
      color: #fff;
    }
  }
  &-selects {
    &__block {
      margin-bottom: 30px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    &__title {
      color: #fff;
    }
  }
  &-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 25px;
    height: 33px;
  }
  &-content {
    height: calc(100vh - 146px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &-menu {
    &__time {
      display: block;
      font-family: "Open Sans", sans-serif;
      color: #fff;
      font-size: 20px;
      font-weight: 500;
    }
    &-link {
      height: 48px;
      display: flex;
      align-items: center;
      margin: 0 -24px;
      padding: 0 24px;
      color: var(--grey);
      text-decoration: none;
      font-size: 14px;
      cursor: pointer;
      &__icon::v-deep {
        margin-right: 14px;
        min-width: 25px;
        max-width: 25px;
        & > path {
          fill: var(--grey);
        }
      }
      &.router-link-active {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
      }
      &__text {
        overflow: hidden;
      }
    }
  }
  .sidebar-select {
    &-content {
      display: flex;
      align-items: center;
    }
    &__icon {
      border-radius: 5px;
    }
    &__selected {
      color: #fff;
      font-size: 18px;
      margin-left: 15px;
      line-height: 1;
    }
    &-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }
    &-option {
      display: flex;
      align-items: center;
      padding: 7px 0;
      &__icon {
        max-width: 25px;
        border-radius: 5px;
        margin-right: 11px;
        display: block;
      }
      &__name {
        line-height: 1;
      }
    }
  }
  .sidebar-select::v-deep {
    & .vs__dropdown-menu {
      top: auto !important;
      bottom: calc(100% + 5px) !important;
      box-shadow: 0px -2px 6px 2px rgba(0, 0, 0, 0.15) !important;
      border-radius: 5px;
      border: none;
      background: #eeeff4;
    }

    & .vs__dropdown-toggle {
      height: 38px;
      border: none;
      color: #fff;
    }

    &.vs--single.vs--open .vs__selected {
      opacity: 1 !important;
    }

    & .vs__actions {
      display: none;
    }

    & .vs__selected {
      width: 100%;
      padding: 0 !important;
    }

    & .vs__selected-options {
      padding: 0 !important;
      margin: 0 !important;
    }

    & .vs__dropdown-option {
      font-size: 18px;
    }

    & .vs__dropdown-option--highlight {
      background: rgba(8, 12, 89, 0.05) !important;
      color: #000;
    }
  }
}
</style>
